import { useEffect, useMemo, useState } from "react";
import { Calendar, Check, Settings } from "lucide-react";
import CustomModal from "@/components/modal/CustomModal";
import ShiftCode from "@/components/shift/ShiftCode";
import type { NurseMonthlyLimitItem } from "@/api/nurses";
import { toast } from "@/components/toast/CustomToast";
import { cn } from "@/utils/cn";
import { getDefaultShiftsSortedBySequence } from "@/utils/shift-util";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  useNurseMonthlyLimits,
  useSaveNurseMonthlyLimits,
} from "@/pages/roster-management/hooks/useNurseMonthlyLimits";

type LimitCode = "N";
type LimitDraft = Record<LimitCode, number | null>;
type LimitShiftMeta = {
  shift_id: string;
  default_shift?: string | null;
  color?: string;
  sequence?: number;
};

const LIMIT_CODES: Array<{
  code: LimitCode;
  label: string;
}> = [{ code: "N", label: "나이트" }];

const NULL_BOUNDS = {
  d_min: null,
  d_max: null,
  d_exact: null,
  e_min: null,
  e_max: null,
  e_exact: null,
  n_min: null,
  n_max: null,
  n_exact: null,
  o_min: null,
  o_max: null,
  o_exact: null,
} satisfies Partial<NurseMonthlyLimitItem>;

const NIGHT_LIMIT_MAX = 15;
const MONTH_OPTIONS = Array.from({ length: 12 }, (_, index) => index + 1);
const NIGHT_LIMIT_MAX_ERROR = `나이트 개수는 ${NIGHT_LIMIT_MAX}개 이하로 설정할 수 있습니다.`;

function getCurrentYearMonth() {
  const now = new Date();
  return { year: now.getFullYear(), month: now.getMonth() + 1 };
}

function getYearOptions(baseYear: number) {
  return Array.from({ length: 7 }, (_, index) => baseYear - 3 + index);
}

function emptyDraft(): LimitDraft {
  return { N: null };
}

function itemToDraft(item?: NurseMonthlyLimitItem | null): LimitDraft {
  return {
    N: item?.n_exact ?? item?.n_min ?? item?.n_max ?? null,
  };
}

function hasMonthlyLimitValue(item: NurseMonthlyLimitItem) {
  return (
    item.d_min != null ||
    item.d_max != null ||
    item.d_exact != null ||
    item.e_min != null ||
    item.e_max != null ||
    item.e_exact != null ||
    item.n_min != null ||
    item.n_max != null ||
    item.n_exact != null ||
    item.o_min != null ||
    item.o_max != null ||
    item.o_exact != null
  );
}

function buildLimitPayload({
  nurseId,
  groupId,
  year,
  month,
  nightExact,
}: {
  nurseId: string;
  groupId: string;
  year: number;
  month: number;
  nightExact: number | null;
}): NurseMonthlyLimitItem {
  return {
    nurse_id: nurseId,
    group_id: groupId,
    year,
    month,
    ...NULL_BOUNDS,
    n_exact: nightExact,
  };
}

function buildSummary(draft: LimitDraft) {
  const parts = LIMIT_CODES.filter(({ code }) => draft[code] != null).map(
    ({ code }) => {
      return `${code} ${draft[code]}개`;
    }
  );
  if (parts.length === 0) return "근무형태 개수 제한없음";
  return `근무형태 제한 설정 | ${parts.join(", ")}`;
}

function getStatusText(value: number | null, active: boolean) {
  if (!active) return "제한없음";
  if (value == null) return "제한없음";
  if (value === 0) return "0개 고정";
  return `${value}개 고정`;
}

interface NurseMonthlyLimitSectionProps {
  nurseId: string | null;
  groupId: string | null;
  nurseName: string;
  teamName?: string;
  selectedMainShifts: string[];
  availableShifts?: LimitShiftMeta[];
  isUseMid?: boolean;
  disabled?: boolean;
}

export default function NurseMonthlyLimitSection({
  nurseId,
  groupId,
  nurseName,
  teamName,
  selectedMainShifts,
  availableShifts,
  disabled,
}: NurseMonthlyLimitSectionProps) {
  const initialYearMonth = useMemo(getCurrentYearMonth, []);
  const [targetDate, setTargetDate] = useState(initialYearMonth);
  const { year, month } = targetDate;
  const yearOptions = useMemo(() => getYearOptions(year), [year]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [draft, setDraft] = useState<LimitDraft>(() => emptyDraft());
  const [validationError, setValidationError] = useState("");
  const [activeCodes, setActiveCodes] = useState<Set<LimitCode>>(
    () => new Set()
  );

  const {
    data: monthlyLimitData,
    currentItem,
    isPending,
  } = useNurseMonthlyLimits({
    year,
    month,
    groupId,
    nurseId,
  });

  const savedDraft = useMemo(() => itemToDraft(currentItem), [currentItem]);
  const registeredMonthSet = useMemo(() => {
    const next = new Set<number>();
    monthlyLimitData?.items.forEach((item) => {
      if (
        item.nurse_id === nurseId &&
        item.group_id === groupId &&
        item.year === year &&
        hasMonthlyLimitValue(item)
      ) {
        next.add(item.month);
      }
    });
    return next;
  }, [groupId, monthlyLimitData?.items, nurseId, year]);

  useEffect(() => {
    setDraft(savedDraft);
    setValidationError("");
    const nextActive = new Set<LimitCode>();
    LIMIT_CODES.forEach(({ code }) => {
      if (savedDraft[code] != null) {
        nextActive.add(code);
      }
    });
    setActiveCodes(nextActive);
  }, [isModalOpen, savedDraft]);

  const enabledShiftSet = useMemo(() => {
    if (selectedMainShifts.length === 0) {
      return new Set(["D", "E", "N"]);
    }
    return new Set(selectedMainShifts);
  }, [selectedMainShifts]);

  const shiftMetaByCode = useMemo(() => {
    const map = new Map<string, { label: string; color?: string }>();
    getDefaultShiftsSortedBySequence(availableShifts ?? []).forEach((shift) => {
      if (!shift.default_shift) return;
      if (!map.has(shift.default_shift)) {
        map.set(shift.default_shift, {
          label: shift.shift_id || shift.default_shift,
          color: shift.color,
        });
      }
    });
    return map;
  }, [availableShifts]);

  const isCodeEnabled = (code: LimitCode) => {
    return enabledShiftSet.has(code);
  };
  const isNightEnabled = isCodeEnabled("N");

  const saveMutation = useSaveNurseMonthlyLimits({
    year,
    month,
    groupId,
    nurseId,
    onSuccess: () => {
      setIsModalOpen(false);
    },
  });
  const { mutate: clearMonthlyLimits, isPending: isClearPending } =
    useSaveNurseMonthlyLimits({
      year,
      month,
      groupId,
      nurseId,
      showToast: false,
    });

  useEffect(() => {
    if (
      isNightEnabled ||
      !nurseId ||
      !groupId ||
      savedDraft.N == null ||
      isClearPending
    ) {
      return;
    }

    setDraft({ N: null });
    setActiveCodes(new Set());
    setValidationError("");
    clearMonthlyLimits({
      year,
      month,
      limits: [
        buildLimitPayload({
          nurseId,
          groupId,
          year,
          month,
          nightExact: null,
        }),
      ],
    });
  }, [
    isNightEnabled,
    nurseId,
    groupId,
    savedDraft.N,
    isClearPending,
    clearMonthlyLimits,
    year,
    month,
  ]);

  const handleValueChange = (code: LimitCode, value: string) => {
    const trimmedValue = value.trim();
    if (trimmedValue !== "" && !/^\d+$/.test(trimmedValue)) return;

    const nextValue = trimmedValue === "" ? null : Number(trimmedValue);
    if (nextValue != null && nextValue > NIGHT_LIMIT_MAX) {
      setValidationError(NIGHT_LIMIT_MAX_ERROR);
      return;
    }

    setDraft((prev) => {
      const next = { ...prev, [code]: nextValue };
      setValidationError("");
      return next;
    });
  };

  const handleToggleCode = (code: LimitCode, checked: boolean) => {
    setActiveCodes((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(code);
      } else {
        next.delete(code);
      }
      return next;
    });

    if (!checked) {
      setValidationError("");
      setDraft((prev) => ({ ...prev, [code]: null }));
      return;
    }
    setValidationError("");
    setDraft((prev) => ({ ...prev, [code]: 0 }));
  };

  const handleSave = () => {
    if (disabled) return;

    if (!nurseId || !groupId) {
      toast.error("근무자 또는 그룹 정보를 확인할 수 없습니다.");
      return;
    }

    if (activeCodes.has("N") && (draft.N ?? 0) > NIGHT_LIMIT_MAX) {
      setValidationError(NIGHT_LIMIT_MAX_ERROR);
      return;
    }
    setValidationError("");

    const limit = buildLimitPayload({
      nurseId,
      groupId,
      year,
      month,
      nightExact:
        isNightEnabled && activeCodes.has("N") ? (draft.N ?? 0) : null,
    });

    saveMutation.mutate({
      year,
      month,
      limits: [limit],
    });
  };

  const summary = buildSummary(isNightEnabled ? savedDraft : emptyDraft());

  return (
    <>
      <div className="mt-3   mb-1 bg-muted/60 px-2 py-1 flex justify-between items-center gap-2">
        <div className="min-w-0 ">
          <p
            className={cn(
              "truncate text-sm font-pretendard text-muted-foreground"
            )}
            title={summary}
          >
            {isPending ? "근무형태 개수 설정 조회 중..." : summary}
          </p>
        </div>

        <button
          type="button"
          onClick={() => setIsModalOpen(true)}
          disabled={!nurseId}
          className="inline-flex p-1 px-2 font-pretendard shrink-0 items-center justify-center gap-1 rounded-[5px] border  bg-white text-[13px] font-semibold leading-[18px] text-muted-foreground transition-colors hover:border-[#0E59C6] hover:text-[#0E59C6] disabled:cursor-not-allowed disabled:opacity-50"
        >
          고급설정
          <Settings className="size-3.5" aria-hidden />
        </button>
      </div>

      {isModalOpen && (
        <CustomModal.Panel
          onClose={() => setIsModalOpen(false)}
          className="w-[450px] bg-white rounded-[10px]"
          button
          btnCancelText="닫기"
          btnSuccessText="저장"
          btnSuccessOnClick={handleSave}
          btnCancelOnClick={() => setIsModalOpen(false)}
          btnSuccessDisabled={disabled || saveMutation.isPending}
          isLoading={saveMutation.isPending}
          dismissOnOverlay={false}
          loadingMsg="근무형태 제한 설정 중..."
        >
          <div className="px-1 font-pretendard pb-5">
            <div className="pt-3 pb-2">
              <h2 className="font-pretendard text-2xl mb-3 text-sig text-center font-semibold">
                고급 설정
              </h2>
            </div>
            <div className=" flex items-center justify-between border-b pb-[3px]">
              <p className="text-sm leading-[18px] text-sig">개수 제한 설정</p>
              <p className="text-sm leading-[18px] text-muted-foreground">
                {nurseName || "간호사"}
                {teamName ? ` | ${teamName}` : ""}
              </p>
            </div>

            <div className="mb-[4px] rounded-[5px] bg-muted/50  text-[13px] p-2 leading-relaxed text-[#6B6B72]">
              <p>제한없음은 알고리즘이 자유롭게 배치합니다. </p>
              <p>0은 해당 근무 횟수를 0개로 고정합니다. </p>
              <p>숫자를 입력하면 해당 근무 횟수가 고정 목표로 적용됩니다. </p>
            </div>

            {validationError && (
              <div className="pt-3">
                {" "}
                <p className="text-destructive text-sm">
                  ! {validationError}
                </p>{" "}
              </div>
            )}

            <div className="border border-border/50 p-3 rounded-md mt-5">
              <div className="flex items-center justify-between gap-3 border-b pb-3  mb-2 border-border/50">
                <div className="flex min-w-0 items-center gap-2 text-sm font-semibold text-muted-foreground">
                  <Calendar className="size-4 shrink-0 ml-1" />
                  <span>적용 월</span>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <Select
                    value={String(year)}
                    onValueChange={(value) =>
                      setTargetDate((prev) => ({
                        ...prev,
                        year: Number(value),
                      }))
                    }
                    disabled={disabled || saveMutation.isPending}
                  >
                    <SelectTrigger className="h-8 w-[96px] rounded-[5px] border-[#D6D6DE] bg-white text-[13px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="min-w-[140px]">
                      {yearOptions.map((optionYear) => (
                        <SelectItem
                          key={optionYear}
                          value={String(optionYear)}
                          className="[&_[data-slot=select-item-indicator]]:hidden"
                        >
                          {optionYear}년
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select
                    value={String(month)}
                    onValueChange={(value) =>
                      setTargetDate((prev) => ({
                        ...prev,
                        month: Number(value),
                      }))
                    }
                    disabled={disabled || saveMutation.isPending}
                  >
                    <SelectTrigger className="h-8 w-[82px] rounded-[5px] border-[#D6D6DE] bg-white text-[13px]">
                      <SelectValue>{month}월</SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {MONTH_OPTIONS.map((optionMonth) => (
                        <SelectItem
                          key={optionMonth}
                          value={String(optionMonth)}
                          className="w-[132px] [&_[data-slot=select-item-indicator]]:hidden"
                        >
                          <div className=" items-center justify-between gap-4 relative inline-block w-[115px] ">
                            <span>{optionMonth}월</span>
                            {registeredMonthSet.has(optionMonth) && (
                              <span className="text-xs  text-muted-foreground  ml-auto absolute right-0 top-[2px]">
                                등록 됨
                              </span>
                            )}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex flex-col">
                {LIMIT_CODES.map(({ code, label }, index) => {
                  const codeEnabled = isCodeEnabled(code);
                  const checked = activeCodes.has(code);
                  const shiftMeta = shiftMetaByCode.get(code);
                  const isLastItem = index === LIMIT_CODES.length - 1;
                  return (
                    <div
                      key={code}
                      aria-disabled={!codeEnabled}
                      className={cn(
                        "grid h-[42px] grid-cols-[1fr_182px] items-center gap-3 rounded-[5px] px-1 transition-colors",
                        !isLastItem && "border-b border-[#F0F0F2]",
                        codeEnabled
                          ? "hover:bg-[#F7F8FA]"
                          : "cursor-not-allowed opacity-45",
                        codeEnabled &&
                          !checked &&
                          "opacity-55 hover:opacity-75 hover:bg-[#F7F8FA]"
                      )}
                    >
                      <div
                        role="button"
                        aria-disabled={disabled || !codeEnabled}
                        tabIndex={!disabled && codeEnabled ? 0 : -1}
                        onClick={() => {
                          if (!disabled && codeEnabled) {
                            handleToggleCode(code, !checked);
                          }
                        }}
                        onKeyDown={(e) => {
                          if (disabled || !codeEnabled) return;
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            handleToggleCode(code, !checked);
                          }
                        }}
                        className={cn(
                          "flex h-full items-center gap-2 rounded-[5px]",
                          !disabled && codeEnabled
                            ? "cursor-pointer"
                            : "cursor-not-allowed"
                        )}
                      >
                        <button
                          type="button"
                          disabled={disabled || !codeEnabled}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (disabled) return;
                            handleToggleCode(code, !checked);
                          }}
                          className={cn(
                            "flex size-[18px] items-center justify-center rounded-full transition-colors",
                            checked ? "text-[#0E59C6]" : "text-[#D6D6DE]",
                            !codeEnabled && "cursor-not-allowed"
                          )}
                          aria-label={`${label} ${
                            checked ? "제한없음으로 변경" : "0개 제한 적용"
                          }`}
                        >
                          <Check className="size-[15px]" strokeWidth={3} />
                        </button>
                        <ShiftCode
                          code={shiftMeta?.label ?? code}
                          color={shiftMeta?.color}
                        />
                        <p className="text-[14px] font-semibold leading-[19px] text-[#53535A]">
                          {label}
                        </p>
                      </div>
                      <div className="grid grid-cols-[95px_1fr]  justify-between items-center gap-2 ">
                        <input
                          type="number"
                          min={0}
                          max={NIGHT_LIMIT_MAX}
                          value={
                            !codeEnabled || !checked ? "" : (draft[code] ?? "")
                          }
                          placeholder="-"
                          disabled={disabled || !codeEnabled || !checked}
                          onClick={(e) => e.stopPropagation()}
                          onKeyDown={(e) => e.stopPropagation()}
                          onChange={(e) =>
                            handleValueChange(code, e.target.value)
                          }
                          className={cn(
                            "h-[35px] rounded-[5px] flex-1 border  text-center text-[14px] disabled:cursor-not-allowed disabled:bg-[#F7F8FA]",
                            checked && "text-sig"
                          )}
                        />
                        <div
                          className={cn(
                            "w-[60px] text-right justify-self-end text-[13px] leading-[17px] text-[#53535A]",
                            checked && "text-sig"
                          )}
                        >
                          {codeEnabled
                            ? getStatusText(draft[code], checked)
                            : "미배정"}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </CustomModal.Panel>
      )}
    </>
  );
}
